import React, {useEffect, useRef, useState} from 'react';
import {RefreshCw} from 'lucide-react';
import './PullToRefresh.css';

const reloadPage = () => window.location.reload();
const THRESHOLD = 90;

export default function PullToRefresh({onRefresh = reloadPage}) {
  const [distance, setDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const busy = useRef(false);
  useEffect(() => {
    let gesture = null, timer;
    const reset = () => { gesture = null; setDistance(0); };
    const start = e => {
      reset();
      if (busy.current || window.innerWidth >= 768 || e.touches.length !== 1 || window.scrollY > 0) return;
      if (document.querySelector('[role="dialog"], [aria-modal="true"]')) return;
      if (e.target.closest('input,textarea,select,button,a,[contenteditable="true"],[role="spinbutton"],[draggable="true"]')) return;
      // Let nested lists, wheels and document viewers keep their own scrolling.
      for (let el=e.target; el && el !== document.body; el=el.parentElement) {
        if (/(auto|scroll)/.test(getComputedStyle(el).overflowY) && el.scrollHeight > el.clientHeight) return;
      }
      const touch=e.touches[0];
      if(touch.clientX < 30) return; // reserved for swipe-back
      gesture={x:touch.clientX,y:touch.clientY,distance:0};
    };
    const move = e => {
      if (!gesture) return;
      if (e.touches.length !== 1 || window.scrollY > 0) { reset(); return; }
      const dx=e.touches[0].clientX-gesture.x, dy=e.touches[0].clientY-gesture.y;
      if (dy < 0 || Math.abs(dx)>Math.max(12,dy)) { reset(); return; }
      if (dy < 8) return;
      if(e.cancelable) e.preventDefault();
      gesture.distance=Math.min(120,dy*0.55);
      setDistance(gesture.distance);
    };
    const end = () => {
      const ready=gesture?.distance >= THRESHOLD;
      reset();
      if (!ready || busy.current) return;
      busy.current=true; setRefreshing(true);
      timer=setTimeout(async()=>{
        try { await onRefresh(); }
        finally { busy.current=false; setRefreshing(false); }
      },150);
    };
    document.documentElement.classList.add('io-pull-refresh-enabled');
    document.addEventListener('touchstart',start,{passive:true});
    document.addEventListener('touchmove',move,{passive:false});
    document.addEventListener('touchend',end,{passive:true});
    document.addEventListener('touchcancel',reset,{passive:true});
    return () => {
      clearTimeout(timer);
      document.documentElement.classList.remove('io-pull-refresh-enabled');
      document.removeEventListener('touchstart',start);
      document.removeEventListener('touchmove',move);
      document.removeEventListener('touchend',end);
      document.removeEventListener('touchcancel',reset);
    };
  },[onRefresh]);
  if (!distance && !refreshing) return null;
  return <div className="io-pull-refresh" role="status" style={{'--pull-offset':`${Math.min(distance,70)}px`}}>
    <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} aria-hidden="true"/>
    <span>{refreshing ? 'Wird aktualisiert …' : distance >= THRESHOLD ? 'Loslassen zum Aktualisieren' : 'Zum Aktualisieren ziehen'}</span>
  </div>;
}
